import { useEffect, useRef, useState } from "react";

export function useMobileKeyboardOpen() {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const keyboardOpenRef = useRef(false);

  useEffect(() => {
    keyboardOpenRef.current = isKeyboardOpen;
  }, [isKeyboardOpen]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    if (!isTouchDevice) {
      return;
    }

    const viewport = window.visualViewport;
    let baseHeight = window.innerHeight;
    const threshold = 120;

    const readViewportHeight = () =>
      Math.min(viewport?.height ?? window.innerHeight, window.innerHeight);

    const syncVisualViewportHeight = () => {
      const vvHeight = viewport?.height ?? window.innerHeight;
      const height = Math.min(vvHeight, window.innerHeight);
      document.documentElement.style.setProperty(
        "--vvh",
        `${height * 0.01}px`
      );

      const bottomOffset = Math.max(
        0,
        window.innerHeight - vvHeight - (viewport?.offsetTop ?? 0)
      );
      document.documentElement.style.setProperty(
        "--keyboard-bottom-offset",
        `${bottomOffset}px`
      );
    };

    const updateKeyboardState = () => {
      const viewportHeight = readViewportHeight();
      const heightDiff = baseHeight - viewportHeight;
      setIsKeyboardOpen(heightDiff > threshold);
    };

    let baseWidth = window.innerWidth;

    const handleResize = () => {
      if (window.innerWidth !== baseWidth) {
        baseWidth = window.innerWidth;
        baseHeight = window.innerHeight;
      }
      updateKeyboardState();
    };

    let focusPollId = 0;

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }
      const tag = target.tagName;
      if (
        tag !== "INPUT" &&
        tag !== "TEXTAREA" &&
        !(target as HTMLElement).isContentEditable
      ) {
        return;
      }

      let attempts = 0;
      const maxAttempts = 30;
      const id = ++focusPollId;

      const poll = () => {
        if (id !== focusPollId) {
          return;
        }
        syncVisualViewportHeight();
        updateKeyboardState();
        if (!keyboardOpenRef.current && ++attempts < maxAttempts) {
          requestAnimationFrame(poll);
        }
      };

      requestAnimationFrame(poll);
    };

    const handleFocusOut = () => {
      requestAnimationFrame(updateKeyboardState);
    };

    updateKeyboardState();

    window.addEventListener("resize", handleResize);
    window.addEventListener("focusin", handleFocusIn);
    window.addEventListener("focusout", handleFocusOut);
    viewport?.addEventListener("resize", updateKeyboardState);
    viewport?.addEventListener("scroll", updateKeyboardState);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("focusin", handleFocusIn);
      window.removeEventListener("focusout", handleFocusOut);
      viewport?.removeEventListener("resize", updateKeyboardState);
      viewport?.removeEventListener("scroll", updateKeyboardState);
    };
  }, []);

  return isKeyboardOpen;
}
